import type { CSS } from '../../stitches.config';
import { globalCss, keyframes, styled } from '../../stitches.config';

/** Which edges get the fade. Match roughly to the scroll axis when possible. */
export type FadeEdge =
  | 'all'
  | 'vertical'
  | 'horizontal'
  | 'top'
  | 'bottom'
  | 'start'
  | 'end'
  | 'none';

/** Default space token used for fade size when not specified (e.g. from gutter). */
const FADE_MASK_SIZE_FALLBACK = '$6';

/** Multiplier applied to the fade size (e.g. 1.5 = 50% larger than the given token). */
const FADE_MASK_SIZE_MULTIPLIER = 1.5;

/**
 * Resolves a space token (e.g. $6, $16) to a CSS length 50% larger than the
 * padding, rounded up to the nearest whole value, using the design system's
 * space scale (var(--space-N)) so sizing stays scalable.
 *
 * Exported so a scroll container can pin `scroll-padding` to the same length: without it
 * `scrollIntoView` parks a keyboard-focused child flush against the scrollport, under the fade.
 */
export function fadeMaskSizeCSS(token: string): string {
  const keyStr = token.startsWith('$') ? token.slice(1) : token;
  const px = Number(keyStr);
  if (!Number.isFinite(px) || px < 0) {
    return `var(--space-${keyStr})`;
  }
  const rounded = Math.ceil(px * FADE_MASK_SIZE_MULTIPLIER);
  return `var(--space-${rounded})`;
}

function maskTop(effectiveSize: string): string {
  return `linear-gradient(to bottom, transparent, black ${effectiveSize})`;
}

function maskBottom(effectiveSize: string): string {
  return `linear-gradient(to bottom, black calc(100% - ${effectiveSize}), transparent)`;
}

function maskStart(effectiveSize: string): string {
  return `linear-gradient(to right, transparent, black ${effectiveSize})`;
}

function maskEnd(effectiveSize: string): string {
  return `linear-gradient(to right, black calc(100% - ${effectiveSize}), transparent)`;
}

function maskVertical(effectiveSize: string): string {
  return `${maskTop(effectiveSize)}, ${maskBottom(effectiveSize)}`;
}

function maskHorizontal(effectiveSize: string): string {
  return `${maskStart(effectiveSize)}, ${maskEnd(effectiveSize)}`;
}

export function fadeMaskCSS(
  size: string = FADE_MASK_SIZE_FALLBACK,
  edges: FadeEdge = 'vertical',
): CSS {
  const effectiveSize = fadeMaskSizeCSS(size);
  switch (edges) {
    case 'all':
      return {
        maskImage: `${maskVertical(effectiveSize)}, ${maskHorizontal(effectiveSize)}`,
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      };
    case 'vertical':
      return {
        maskImage: maskVertical(effectiveSize),
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      };
    case 'horizontal':
      return {
        maskImage: maskHorizontal(effectiveSize),
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      };
    case 'top':
      return { maskImage: maskTop(effectiveSize) };
    case 'bottom':
      return { maskImage: maskBottom(effectiveSize) };
    case 'start':
      return { maskImage: maskStart(effectiveSize) };
    case 'end':
      return { maskImage: maskEnd(effectiveSize) };
    case 'none':
      return { maskImage: 'none' };
    default: {
      const exhaustiveCheck: never = edges;
      throw new Error(`Unexpected fade edge: ${String(exhaustiveCheck)}`);
    }
  }
}

type OverflowFadeSide = 'top' | 'bottom' | 'start' | 'end';

const OVERFLOW_FADE_SIDES: Record<FadeEdge, OverflowFadeSide[]> = {
  all: ['top', 'bottom', 'start', 'end'],
  vertical: ['top', 'bottom'],
  horizontal: ['start', 'end'],
  top: ['top'],
  bottom: ['bottom'],
  start: ['start'],
  end: ['end'],
  none: [],
};

// A finished scroll-driven animation holds its value once the scroller stops overflowing, so each
// side tracks its scroll distance over a range no real scroller reaches, finishing only at 0.
const OVERFLOW_FADE_TRACK_LENGTH = '100000px';

const OVERFLOW_FADE_PROPERTY = { syntax: "'<length>'", inherits: false, initialValue: '0px' };

// Registered so the animations interpolate them; an unregistered custom property flips at the midpoint.
const registerOverflowFadeProperties = globalCss({
  '@property --fade-mask-top': OVERFLOW_FADE_PROPERTY,
  '@property --fade-mask-bottom': OVERFLOW_FADE_PROPERTY,
  '@property --fade-mask-start': OVERFLOW_FADE_PROPERTY,
  '@property --fade-mask-end': OVERFLOW_FADE_PROPERTY,
});

function isVerticalSide(side: OverflowFadeSide): boolean {
  return side === 'top' || side === 'bottom';
}

function isLeadingSide(side: OverflowFadeSide): boolean {
  return side === 'top' || side === 'start';
}

function overflowFadeKeyframes(side: OverflowFadeSide) {
  const property = `--fade-mask-${side}`;
  const [from, to] = isLeadingSide(side)
    ? ['0px', OVERFLOW_FADE_TRACK_LENGTH]
    : [OVERFLOW_FADE_TRACK_LENGTH, '0px'];
  return keyframes({ from: { [property]: from }, to: { [property]: to } });
}

const OVERFLOW_FADE_KEYFRAMES: Record<OverflowFadeSide, ReturnType<typeof keyframes>> = {
  top: overflowFadeKeyframes('top'),
  bottom: overflowFadeKeyframes('bottom'),
  start: overflowFadeKeyframes('start'),
  end: overflowFadeKeyframes('end'),
};

/**
 * Like `fadeMaskCSS`, but an edge fades only while content is scrolled past it, ramping in over
 * the fade's length. Scroll-driven animations do the measuring; browsers without them get no fade.
 */
export function overflowFadeMaskCSS(
  size: string = FADE_MASK_SIZE_FALLBACK,
  edges: FadeEdge = 'vertical',
): CSS {
  const sides = OVERFLOW_FADE_SIDES[edges];
  if (sides.length === 0) {
    return { maskImage: 'none' };
  }
  registerOverflowFadeProperties();

  const effectiveSize = fadeMaskSizeCSS(size);
  const inset = (side: OverflowFadeSide) => `min(${effectiveSize}, var(--fade-mask-${side}, 0px))`;
  const masks: string[] = [];
  if (sides.some(isVerticalSide)) {
    masks.push(
      `linear-gradient(to bottom, transparent, black ${inset('top')}, black calc(100% - ${inset('bottom')}), transparent)`,
    );
  }
  if (!sides.every(isVerticalSide)) {
    masks.push(
      `linear-gradient(to right, transparent, black ${inset('start')}, black calc(100% - ${inset('end')}), transparent)`,
    );
  }

  return {
    '@supports (animation-timeline: scroll())': {
      maskImage: masks.join(', '),
      ...(masks.length > 1 ? { maskComposite: 'intersect', WebkitMaskComposite: 'source-in' } : {}),
      animationName: sides.map((side) => `${OVERFLOW_FADE_KEYFRAMES[side]}`).join(', '),
      animationFillMode: 'both',
      animationTimingFunction: 'linear',
      animationTimeline: sides
        .map((side) => (isVerticalSide(side) ? 'scroll(self y)' : 'scroll(self x)'))
        .join(', '),
      animationRange: sides
        .map((side) =>
          isLeadingSide(side)
            ? `0 ${OVERFLOW_FADE_TRACK_LENGTH}`
            : `calc(100% - ${OVERFLOW_FADE_TRACK_LENGTH}) 100%`,
        )
        .join(', '),
    },
  };
}

export const HorizontalScrollFade = styled('div', overflowFadeMaskCSS('$8', 'horizontal'), {
  maxWidth: '100%',
  overflow: 'auto',
});
HorizontalScrollFade.displayName = 'HorizontalScrollFade';

export const VerticalScrollFade = styled('div', overflowFadeMaskCSS('$8', 'vertical'), {
  maxHeight: '100%',
  overflow: 'auto',
});
VerticalScrollFade.displayName = 'VerticalScrollFade';
